import { FormColumns, FormLabel } from "components/form";
import {
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "ui/components/form";
import ClientInput from "components/client/input";
import EmployeeInput from "components/employee/input";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import SelectName from "components/select/name";
import React from "react";
import { Switch } from "ui/components/switch";
import { projectSchema } from "./schema";
import { useHotkeys } from "react-hotkeys-hook";
import { useProjectForm } from "@/modules/work/projects/project/crud/form/useProjectForm";
import FileInput from "components/file-upload/input";
import { FormActions } from "components/form/actions";
import { StatusTabInput } from "components/status/tabs/input";


import ProjectTagsCombobox from "components/directus/combobox/project-tags";
import SubIndustryCombobox from "components/directus/combobox/sub-industry";
import DirectusMultiComboboxBadges from "components/directus/multi-combobox-badges";
import client from "directus/src/client";
import { DatePicker } from "components/date-picker-2";
import { Textarea } from "ui/components/textarea";
import { Button } from "ui/components/button";
import { useFieldArray } from "react-hook-form";

const mapCountry = (item) => ({
  value: item.id,
  label: item.name,
  select: item.name,
});

const ProjectForm = ({ projectId }) => {
  const {
    form,
    requiredFields,
    canReadClient,
    handleCreate,
    handleRead,
    handleUpdate,
  } = useProjectForm({ projectId });

  const {
    fields: statisticFields,
    append: appendStatistic,
    remove: removeStatistic,
  } = useFieldArray({ control: form.control, name: "statistics" });

  const startDate = form.watch("start_date");

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={projectId || "+"}
      schema={projectSchema}
    >
      {({
        isCreating,
        createItem,
        isNewItem,
        isUpdating,
        updateItem,
        isDirty,
      }) => {
        useHotkeys(
          "ctrl+s, cmd+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(false);
              createItem().then(handleClose);
            }
          },
          { preventDefault: true },
        );

        useHotkeys(
          "ctrl+shift+s, cmd+shift+s",
          (event) => {
            event.preventDefault();
            if (!isCreating) {
              setIsNew(true);
              createItem();
            }
          },
          { preventDefault: true },
        );
        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Status
                      </FormLabel>

                      <StatusTabInput
                        {...field}
                        collection="projects"
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="logo"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Upload Logo
                      </FormLabel>

                      <FileInput
                        className="size-44"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => {
                return (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Name
                      </FormLabel>

                      <Input {...field} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:none"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Project ID
                    </FormLabel>

                    <Input readOnly={true} disabled={!!projectId} {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

               <FormColumns>
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        Start Date
                      </FormLabel>
                      <DatePicker value={field.value} onChange={field.onChange} />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <PermissionControlledInput
                      rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                      collection="projects"
                      type="form"
                      field={field.name}
                    >
                      <FormLabel required={requiredFields.includes(field.name)}>
                        End Date
                      </FormLabel>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        min={startDate ? String(startDate).slice(0, 10) : undefined}
                      />
                    </PermissionControlledInput>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormColumns>


            <FormField
              control={form.control}
              name="alias"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Alias
                    </FormLabel>
                    <Input {...field} value={field.value ?? ""} />
                  </PermissionControlledInput>
                  <FormDescription>
                    Short alternate or public-facing name for the project (e.g.
                    a codename or display name used on case studies and
                    portfolio).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

        

            {canReadClient && (
              <FormField
                control={form.control}
                name="client"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <PermissionControlledInput
                        rule={isNewItem ? "create:*" : "update:*"}
                        collection="projects"
                        type="form"
                        field={field.name}
                      >
                        <FormLabel
                          required={requiredFields.includes(field.name)}
                        >
                          Client
                        </FormLabel>
                        <ClientInput {...field} />
                      </PermissionControlledInput>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            {/* Coordinators */}
            <FormField
              control={form.control}
              name="sales_coordinator"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Sales Coordinator
                    </FormLabel>
                    <EmployeeInput set="sales_coordinating" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="account_manager"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Account Manager
                    </FormLabel>
                    <EmployeeInput set="account_manager" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coordinator"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:*"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Project Coordinator
                    </FormLabel>
                    <EmployeeInput set="projects_coordinating" {...field} />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="legal_entity"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Entity
                    </FormLabel>
                    <SelectName
                      collection="legal_entities"
                      fetchItems={true}
                      onValueChange={(val) => field.onChange(val)}
                      {...field}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

                <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Industry
                    </FormLabel>
                    <SubIndustryCombobox
                      hideLabel
                      className="w-full"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="countries"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Countries
                    </FormLabel>
                    <DirectusMultiComboboxBadges
                      client={client}
                      collection="countries"
                      fields={["id", "name"]}
                      searchKeys={["name"]}
                      sort={["name"]}
                      mapItem={mapCountry}
                      placeholder="Select Countries"
                      searchPlaceholder="Search countries..."
                      initialValues={field.value || []}
                      onValuesChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormDescription>
                    Countries this project is delivered for or operates in.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

         
            <FormField
              control={form.control}
              name="about"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      About
                    </FormLabel>
                    <Textarea rows={4} className="min-h-40" {...field} value={field.value ?? ""} />
                  </PermissionControlledInput>
                  <FormDescription>
                    Short plain-text overview of the project: what it is and
                    what it does. Project-related information only; DO NOT add
                    sensitive information like cost, emails, contact numbers,
                    client names etc.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

              <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput
                    rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
                    collection="projects"
                    type="form"
                    field={field.name}
                  >
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Tags
                    </FormLabel>
                    <ProjectTagsCombobox
                      isMulti
                      hideLabel
                      values={field.value}
                      onValuesChange={field.onChange}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </PermissionControlledInput>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Statistics (Directus repeater: [{ label, value }]) */}
            <PermissionControlledInput
              rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
              collection="projects"
              type="form"
              field="statistics"
            >
              <div className="space-y-3">
                <FormLabel required={requiredFields.includes("statistics")}>
                  Statistics
                </FormLabel>
                {statisticFields.map((item, index) => (
                  <div key={item.id} className="flex items-start gap-2">
                    <FormField
                      control={form.control}
                      name={`statistics.${index}.value`}
                      render={({ field }) => (
                        <FormItem className="w-32">
                          <Input placeholder="Value (e.g. 50K+)" {...field} />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`statistics.${index}.label`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <Input placeholder="Label (e.g. Active users)" {...field} />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeStatistic(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => appendStatistic({ label: "", value: "" })}
                >
                  Add Statistic
                </Button>
              </div>
            </PermissionControlledInput>

          

            <FormActions
              rule={isNewItem ? "create:*" : "update:PC,SC,AM"}
              collection="projects"
              isNewItem={isNewItem}
              isCreating={isCreating}
              createItem={createItem}
              isDirty={isDirty}
              updateItem={updateItem}
              isUpdating={isUpdating}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default ProjectForm;
